/**
 * EditableGridHead.tsx — the header row of EditableGrid, extracted whole when
 * the freeze/sticky work (BUG-2026-0159) pushed the grid past the 600-line
 * cap. Renders the select-all checkbox gutter plus one sortable/filterable
 * cell per column, with the opt-in sticky-top (stickyHeader) and sticky-left
 * (frozen columns) treatments layered on via gridFreeze helpers.
 */
import {
  Box,
  Checkbox,
  TableCell,
  TableHead,
  TableRow,
  TableSortLabel,
} from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import type { GridColumn } from './EditableGrid';
import { GridColumnFilter, type GridFilterValueOption } from './GridColumnFilter';
import { frozenEdgeSx, layeredBg } from './gridFreeze';

interface EditableGridHeadProps {
  columns: GridColumn[];
  cellBorder: string;
  sortField: string | null;
  sortDir: 'asc' | 'desc';
  onSortClick: (field: string) => void;
  enableColumnFilters: boolean;
  filterOptionsByField: Record<string, GridFilterValueOption[]>;
  columnFilters: Record<string, Set<string>>;
  onFilterChange: (field: string, next: Set<string> | null) => void;
  selectAllChecked: boolean;
  selectAllIndeterminate: boolean;
  onToggleAll: () => void;
  /** Leading data columns frozen sticky-left (0 = off). */
  freezeCount: number;
  /**
   * Sticky `left` per frozen data column, measured from the table's real
   * layout by EditableGrid (config estimate until the first measure). Never
   * clamp frozen header widths to config — the label + sort arrow + filter
   * funnel can be wider, and clamping clips the funnel (BUG-2026-0159 rework).
   */
  frozenOffsets: number[];
  /** Keep the header row sticky-top inside the scrolling container. */
  stickyHeader: boolean;
}

export const EditableGridHead = ({
  columns,
  cellBorder,
  sortField,
  sortDir,
  onSortClick,
  enableColumnFilters,
  filterOptionsByField,
  columnFilters,
  onFilterChange,
  selectAllChecked,
  selectAllIndeterminate,
  onToggleAll,
  freezeCount,
  frozenOffsets,
  stickyHeader,
}: EditableGridHeadProps) => {
  const theme = useTheme();
  const headerTint = alpha(theme.table.headerBg, 0.7);

  return (
    <TableHead>
      <TableRow sx={{ backgroundColor: headerTint }}>
        <TableCell
          padding="checkbox"
          sx={{
            borderBottom: cellBorder,
            width: 42,
            ...((stickyHeader || freezeCount > 0) && {
              position: 'sticky',
              ...(stickyHeader && { top: 0 }),
              ...(freezeCount > 0 && { left: 0 }),
              zIndex: freezeCount > 0 ? 4 : 3,
              ...layeredBg(theme, headerTint),
            }),
          }}
        >
          <Checkbox
            size="small"
            indeterminate={selectAllIndeterminate}
            checked={selectAllChecked}
            onChange={onToggleAll}
          />
        </TableCell>
        {columns.map((col, colIdx) => {
          const isSortable = col.type !== 'actions' && col.headerName !== '';
          const isFrozen = colIdx < freezeCount;
          return (
            <TableCell
              key={col.field}
              sortDirection={sortField === col.field ? sortDir : false}
              sx={{
                fontWeight: 600, fontSize: '0.7rem', textTransform: 'uppercase',
                letterSpacing: '0.03em', color: alpha(theme.palette.text.primary, 0.6),
                borderBottom: cellBorder, whiteSpace: 'nowrap',
                ...(col.width && { minWidth: col.width, width: col.width }),
                ...(col.flex && { width: '100%' }),
                ...((stickyHeader || isFrozen) && {
                  position: 'sticky',
                  ...(stickyHeader && { top: 0 }),
                  ...(isFrozen && { left: frozenOffsets[colIdx] }),
                  zIndex: isFrozen ? 4 : 3,
                  ...layeredBg(theme, headerTint),
                  ...(isFrozen && colIdx === freezeCount - 1 && frozenEdgeSx(theme)),
                }),
              }}
            >
              <Box sx={{ display: 'inline-flex', alignItems: 'center' }}>
                {isSortable ? (
                  <TableSortLabel
                    active={sortField === col.field}
                    direction={sortField === col.field ? sortDir : 'asc'}
                    onClick={() => onSortClick(col.field)}
                    sx={{ fontSize: 'inherit', fontWeight: 'inherit', textTransform: 'inherit' }}
                  >
                    {col.headerName}
                  </TableSortLabel>
                ) : col.headerName}
                {enableColumnFilters && isSortable && (
                  <GridColumnFilter
                    headerName={col.headerName}
                    values={filterOptionsByField[col.field] ?? []}
                    selected={columnFilters[col.field] ?? null}
                    onChange={(next) => onFilterChange(col.field, next)}
                  />
                )}
              </Box>
            </TableCell>
          );
        })}
      </TableRow>
    </TableHead>
  );
};
